// /////////////////////////////////////////////////////////////
// [ファイル] SalesProjectsCreateNew.tsx
// [概要] 営業案件作成 CloudSQL API リクエスト
// /////////////////////////////////////////////////////////////
import React, { useState, useEffect } from 'react';
import { useAuth } from "../firebase/AuthProvider";
import { usePermissions } from "../firebase/PermissionProvider";
import axios from 'axios';
import LoadingView from '../components/LoadingView';
import {
  Box,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from "../constants/icons";
import SalesProjectFormFields from '../components/SalesProjectFormFields';
import { useNavigate, useLocation } from 'react-router-dom';
import { validateSalesProject, ValidationErrors } from '../utils/validation';
import { useMasterData } from '../providers/MasterDataProvider';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import { ep } from '../api/endpoints';

// --------------------------------------------------
// 📝 営業案件作成
// --------------------------------------------------
export default function SalesProjectsCreateNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { staffList, loading: masterLoading } = useMasterData();
  const [currentTab, setCurrentTab] = useState('basic');
  // 仕入案件詳細の「売上案件を作成」導線から親案件・取引先を引き継ぐ
  const preset = (location.state || {}) as {
    presetParentId?: string;
    presetParentName?: string;
    presetClientId?: string;
    presetClientName?: string;
  };
  const [formData, setFormData] = useState<any>({
    project_name: '',
    client_id: preset.presetClientId || '',
    client_name: preset.presetClientName || '',
    project_type: preset.presetParentId ? 'sales' : '',
    parent_project_id: preset.presetParentId || null,
    parent_project_name: preset.presetParentName || '',
    record_type: '',
    category_large: '',
    category_large_extra: '',
    category_medium: '',
    category_medium_extra: '',
    demolition_land_prep: '',
    phase: '1.見込み',
    order_confirmed_date: '',
    appointment_method: '',
    main_staff_user_id: user?.email || '',
    main_staff_name: '',
    main_staff_department: '',
    main_staff_branch: '',
    owner_id: user?.email || '',
    owner_name: '',
    owner_department: '',
    owner_branch: '',
    support_staff_id: '',
    support_staff_name: '',
    support_staff_department: '',
    support_staff_branch: '',
    location: '',
    new_system_number: [],
    // 分筆・合筆なしの既定状態 (1 筆 → 1 筆)
    parcel_count_before: '1',
    parcel_count_after: '1',
  });

  // バリデーションエラー
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // 権限取得
  const { permissionData, loading: permsLoading } = usePermissions();
  const permissions = permsLoading ? null : permissionData;
  const viewLevel = !permissions
    ? 'individual'
    : permissions.includes('readdata.sfa.all_view')
      ? 'all'
      : permissions.includes('readdata.sfa.branch_view')
        ? 'branch'
        : 'individual';

  // ダイアログ・スナックバーの状態
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean, message: string, severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const CREATE_PROJECT_URL = ep('create_sales_project');

  // --------------------------------------------------
  // 👤 ログインユーザー情報を自動セット (Member & Owner)
  // --------------------------------------------------
  useEffect(() => {
    if (!masterLoading && staffList.length > 0 && user?.email) {
      const currentUserStaff = staffList.find(s => s.staff_id === user.email);
      if (currentUserStaff) {
        setFormData((prev: any) => {
          // 既にセットされている場合は上書きしない
          if (prev.main_staff_name && prev.owner_name) {
            return prev;
          }

          return {
            ...prev,
            // Main Staff
            main_staff_user_id: currentUserStaff.staff_id,
            main_staff_name: currentUserStaff.staff_name,
            main_staff_department: currentUserStaff.department,
            main_staff_branch: currentUserStaff.branch,
            // Owner
            owner_id: currentUserStaff.staff_id,
            owner_name: currentUserStaff.staff_name,
            owner_department: currentUserStaff.department,
            owner_branch: currentUserStaff.branch,
          };
        });
      }
    }
  }, [staffList, user, masterLoading]);

  // --------------------------------------------------
  // 📝 登録処理
  // --------------------------------------------------
  const handleSubmit = async () => {
    const errors = validateSalesProject(formData);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSnackbar({ open: true, message: '入力内容を確認してください', severity: 'error' });
      // 基本情報タブにエラーがある場合はタブを切り替える
      if (errors.project_name || errors.client_id || errors.project_type || errors.parent_project_id || errors.order_confirmed_date) {
        setCurrentTab('basic');
      }
      return;
    }

    setConfirmDialogOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setConfirmDialogOpen(false);
    try {
      setLoading(true);
      setError(null);

      // 送信データの構築
      const payload = {
        ...formData,
        updated_operator_id: user?.email
      };

      const result = await axios.post(CREATE_PROJECT_URL, payload);

      setResponse(result.data);
      // alert('正常に登録されました');
      setSnackbar({ open: true, message: '正常に登録されました', severity: 'success' });

      // 少し待ってから遷移
      setTimeout(() => {
        navigate('/salesprojectsmanagement');
      }, 1500);

    } catch (err: any) {
      console.error("Registration Error:", err);
      setError(err.message);
      // alert('エラーが発生しました: ' + (err.response?.data?.message || err.message));
      setSnackbar({
        open: true,
        message: 'エラーが発生しました: ' + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // 🔁 Loding view
  // --------------------------------------------------
  if (loading || masterLoading) {
    return <LoadingView message={masterLoading ? "初期データを準備中..." : "登録中..."} height="80vh" size={50} />;
  };

  return (
    <Box sx={{ py: 3, width: '70%', margin: '0 auto' }}>
      {/* ヘッダーカード */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: 1,
          p: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 35,
              height: 35,
              borderRadius: 2,
              bgcolor: '#e8f5e9',
              color: '#2e7d32',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.PostAdd sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: blueGrey[800], lineHeight: 0.5 }}>
              新規案件の登録
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}
            >
              FILL IN THE DETAILS BELOW
            </Typography>
          </Box>
        </Box>
        <Tooltip title="案件リストへ戻る" arrow>
          <IconButton
            onClick={() => navigate('/salesprojectsmanagement')}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              color: 'text.secondary',
            }}
          >
            <Icons.Close />
          </IconButton>
        </Tooltip>
      </Box>

      {/* フォームコンポーネント */}
      <Box sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: 1, p: 2.5 }}>
        <SalesProjectFormFields
          formData={formData}
          onChange={(field: string, value: any) => {
            setFormData((prev: any) => ({ ...prev, [field]: value }));
            // 入力時にそのフィールドのエラーを消す
            if (validationErrors[field]) {
              setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
              });
            }
          }}
          currentTab={currentTab}
          onTabChange={(newValue: string) => setCurrentTab(newValue)}
          errors={validationErrors}
          viewLevel={viewLevel}
        />
      </Box>

      {/* フッターカード */}
      <Box
        sx={{
          mt: 2,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: 1,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.disabled' }}>
          <Icons.HelpOutline fontSize="small" />
          <Typography variant="caption">
            入力内容は自動保存されません。必ず「保存する」ボタンを押してください。
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => navigate('/salesprojectsmanagement')} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button startIcon={<Icons.Save />} onClick={handleSubmit} sx={STANDARD_CTA_BUTTON_SX}>
            保存する
          </Button>
        </Box>
      </Box>

      {error && (
        <div style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>
          <strong>エラー:</strong> {error}
        </div>
      )}

      {response && (
        <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f0f0f0' }}>
          <h6>レスポンス内容:</h6>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '14px' }}>
            {typeof response === 'object' ? JSON.stringify(response, null, 2) : String(response)}
          </pre>
        </div>
      )}

      {/* 登録確認ダイアログ */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>登録の確認</DialogTitle>
        <DialogContent>
          <Typography>この内容で案件を登録してもよろしいですか？</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button onClick={handleConfirmSubmit} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX} autoFocus>
            登録する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知用スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
